import type { GeoLocation } from '../Store'
import { Venue } from './venueData'

interface GeoPoint {
  lat: number
  lon: number
}

const EARTH_RADIUS_METERS = 6371000

/**
 * Calculates the distance between two GPS coordinates using the Haversine formula.
 * @param p1 The first point.
 * @param p2 The second point.
 * @returns The distance in meters.
 */
export function getPointDistance(p1: GeoPoint, p2: GeoPoint): number {
  const dLat = (p2.lat - p1.lat) * (Math.PI / 180)
  const dLon = (p2.lon - p1.lon) * (Math.PI / 180)
  const lat1 = p1.lat * (Math.PI / 180)
  const lat2 = p2.lat * (Math.PI / 180)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return EARTH_RADIUS_METERS * c
}

export interface ClosestVenue {
  venueId: string // e.g. TAB
  locationId: number // e.g. 630
  name: string // e.g. "The Crown Casino"
  distance: number // in meters
  inside: boolean // true if the coordinates are matched to within the agency radius
  radius: number
}

/**
 * Finds closest venue to a given position, then checks if position is inside geofence
 * @param position The user's location to check.
 * @param venueList The array of tab venues to with locations.
 * @param radius Maximum distance considered to be 'inside' the venue
 */
export const findClosestVenue = (position: GeoLocation, venueList: Venue[]): ClosestVenue => {
  interface ClosestResult {
    venue: Venue
    distance: number
    radius: number
  }

  const userPosition: GeoPoint = { lat: Number(position.latitude), lon: Number(position.longitude) }

  let closestVenue: Venue = venueList[0]
  let minDistance: number = getPointDistance(userPosition, {
    lat: closestVenue.latitude,
    lon: closestVenue.longitude,
  })

  for (let i = 1; i < venueList.length; i++) {
    const venue = venueList[i]
    const distance = getPointDistance(userPosition, { lat: venue.latitude, lon: venue.longitude })
    if (distance < minDistance) {
      closestVenue = venue
      minDistance = distance
    }
  }

  const result: ClosestResult = {
    venue: closestVenue,
    distance: minDistance,
    radius: closestVenue.radius,
  }

  return {
    venueId: result.venue.venue_id,
    locationId: result.venue.location_id ?? 0,
    name: result.venue.location_name,
    distance: Math.round(result.distance),
    inside: result.distance < result.venue.radius,
    radius: result.radius,
  }
}
